import React, { useState } from 'react'
import { X, ArrowLeft } from 'lucide-react'
import { satsueiDataTorokuService } from '../../services/satsueiDataTorokuService'
import ImageCard from '../ImageCard'
import toast from 'react-hot-toast'
import { useBlobUrlFromApi } from '../../hooks/useBlobUrl'
import { useApiError } from '../../../shared/hooks/useApiError'

// Separate component for image display to handle async loading
const ImageDisplay = ({ image }) => {
  const { blobUrl, isLoading, error } = useBlobUrlFromApi(
    () => satsueiDataTorokuService.getImage(
      image.objectId,
      image.objectImageId,
      image.objectImageEditId,
      image.filenameExtension
    ),
    [image.objectId, image.objectImageId, image.objectImageEditId, image.filenameExtension],
    'image/jpeg'
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full" data-testid="image-loading">
        <div className="text-gray-500 text-xs">読み込み中...</div>
      </div>
    )
  }

  if (error || !blobUrl) {
    return (
      <div className="flex items-center justify-center w-full h-full" data-testid="image-error">
        <div className="text-gray-500 text-xs">画像の読み込みに失敗しました</div>
      </div>
    )
  }

  return (
    <img
      src={blobUrl}
      alt={`合成画像 ${image.objectId || 'unknown'}`}
      className="w-full h-full object-cover"
      onError={() => console.error('Image failed to load')}
      data-testid="composite-image"
    />
  )
}

const CompositeImageModal = ({ isOpen, onClose, imageList, objectId, onCompositeComplete }) => {
  const { handleApiResponse } = useApiError()
  const [selectedImages, setSelectedImages] = useState([])
  const [currentImageIndex, setCurrentImageIndex] = useState(1)
  const [ratio, setRatio] = useState('1') // 1: rectangle, 2: square
  const [saveOption, setSaveOption] = useState('1') // 1: keep original, 2: delete original
  const [isLoading, setIsLoading] = useState(false)

  const handleImageSelect = (image) => {
    if (selectedImages.length >= 4) {
      toast.error('最大4枚まで選択できます')
      return
    }

    setSelectedImages([...selectedImages, image])
  }


  const handleBack = () => {
    if (selectedImages.length > 0) {
      // Remove the last selected image
      const newSelectedImages = selectedImages.slice(0, -1)
      setSelectedImages(newSelectedImages)
      // Set current index to the next available slot (which is the length of remaining images + 1)
      setCurrentImageIndex(newSelectedImages.length + 1)
    }
  }

  const handleStartOver = () => {
    setSelectedImages([])
    setCurrentImageIndex(1)
  }

  const handleSave = async () => {
    if (selectedImages.length === 0) {
      toast.error('画像を選択してください')
      return
    }

    setIsLoading(true)
    try {
      const data = {
        objectId,
        compImageRatio: ratio,
        isDeleteSrcImage: saveOption,
        compImageInfoList: selectedImages.map(img => ({
          objectId: img.objectId,
          objectImageId: img.objectImageId,
          objectImageEditId: img.objectImageEditId,
          filenameExtension: img.filenameExtension
        }))
      }

      const response = await satsueiDataTorokuService.compositeImage(data)
      
      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }
      
      toast.success('合成画像が正常に作成されました')
      onCompositeComplete()
      
      // Reset modal state before closing
      setSelectedImages([])
      setCurrentImageIndex(1)
      setRatio('1')
      setSaveOption('1')
      onClose()
    } catch (error) {
      console.error('Failed to create composite image:', error)
      toast.error('合成画像の作成に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedImages([])
    setCurrentImageIndex(1)
    setRatio('1')
    setSaveOption('1')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="composite-image-modal">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl h-[95vh] sm:h-5/6 flex flex-col" data-testid="modal-content">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">画像4分割合成</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-modal-button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Left Panel - Image Selection */}
          <div className="flex-1 p-3 sm:p-4 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-hidden lg:flex-1" data-testid="image-selection-panel">
            <h4 className="text-sm font-medium text-gray-700 mb-3">画像選択</h4>
            <div className="grid gap-2 sm:gap-3 md:gap-4 overflow-y-auto" 
                 data-testid="image-selection-grid"
                 style={{
                   gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                   gridAutoRows: 'minmax(120px, auto)',
                   maxHeight: 'calc(100vh - 180px)'
                 }}>
              {imageList.map((image) => {
                const isSelected = selectedImages.find(img => img.objectImageId === image.objectImageId)
                return (
                  <div
                    key={image.objectImageId}
                    className={`transition-all cursor-pointer flex justify-center ${
                      isSelected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                    onClick={() => handleImageSelect(image)}
                    data-testid={`image-selection-item-${image.objectImageId}`}
                  >
                    <ImageCard 
                      image={image} 
                      imageService={satsueiDataTorokuService}
                      pageType="satsueiDataToroku"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right Panel - Preview and Settings */}
          <div className="w-full lg:w-80 p-3 lg:p-4 flex flex-col overflow-y-auto" data-testid="preview-settings-panel">
            {/* Preview */}
            <div className="mb-4 lg:mb-6" data-testid="preview-section">
              <h4 className="text-sm font-medium text-gray-700 mb-2 lg:mb-3">プレビュー</h4>
              <div className={`grid grid-cols-2 gap-2 ${
                ratio === '2' 
                  ? 'aspect-square w-48 sm:w-56 lg:w-64 mx-auto' 
                  : 'aspect-[4/3] w-48 sm:w-56 lg:w-64 mx-auto'
              }`} data-testid="preview-grid">
                {[1, 2, 3, 4].map((index) => {
                  const image = selectedImages[index - 1]
                  return (
                    <div
                      key={index}
                      className={`border-2 border-dashed rounded ${
                        image ? 'border-blue-500' : 'border-gray-300'
                      } flex items-center justify-center ${
                        currentImageIndex === index ? 'bg-blue-50' : 'bg-gray-50'
                      } relative overflow-hidden ${
                        ratio === '2' ? 'aspect-square' : 'aspect-[2/1.5]'
                      }`}
                      data-testid={`preview-slot-${index}`}
                    >
                      {image ? (
                        <div className="w-full h-full relative" data-testid={`image-display-${index}`}>
                          <ImageDisplay image={image} />
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs" data-testid={`placeholder-${index}`}>画像 {index}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Settings */}
            <div className="flex-1 space-y-2 lg:space-y-4" data-testid="settings-section">
              {/* Ratio Selection */}
              <div data-testid="ratio-selection">
                <h4 className="text-sm font-medium text-gray-700 mb-1 lg:mb-2">比率</h4>
                <div className="flex space-x-3 lg:space-x-4">
                  <label className="flex items-center" htmlFor="ratio-rectangle">
                    <input
                      type="radio"
                      id="ratio-rectangle"
                      value="1"
                      checked={ratio === '1'}
                      onChange={(e) => setRatio(e.target.value)}
                      className="mr-1 lg:mr-2"
                      data-testid="ratio-rectangle-radio"
                    />
                    <span className="text-xs lg:text-sm">長方形</span>
                  </label>
                  <label className="flex items-center" htmlFor="ratio-square">
                    <input
                      type="radio"
                      id="ratio-square"
                      value="2"
                      checked={ratio === '2'}
                      onChange={(e) => setRatio(e.target.value)}
                      className="mr-1 lg:mr-2"
                      data-testid="ratio-square-radio"
                    />
                    <span className="text-xs lg:text-sm">正方形</span>
                  </label>
                </div>
              </div>

              {/* Save Option */}
              <div data-testid="save-option-selection">
                <h4 className="text-sm font-medium text-gray-700 mb-1 lg:mb-2">保存オプション</h4>
                <select
                  value={saveOption}
                  onChange={(e) => setSaveOption(e.target.value)}
                  className="w-full px-2 lg:px-3 py-1.5 lg:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs lg:text-sm"
                  data-testid="save-option-select"
                >
                  <option value="1">合成元画像を残して保存</option>
                  <option value="2">合成元画像を消して保存</option>
                </select>
              </div>

              {/* Controls */}
              <div className="space-y-1.5 lg:space-y-2" data-testid="controls-section">
                <div className="grid grid-cols-2 gap-1.5 lg:gap-2">
                  <button
                    onClick={handleBack}
                    disabled={selectedImages.length === 0}
                    className="px-2 lg:px-3 py-1.5 lg:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 text-xs lg:text-sm"
                    data-testid="back-button"
                  >
                    <ArrowLeft size={12} className="inline mr-1" />
                    戻る
                  </button>
                  <button
                    onClick={handleStartOver}
                    className="px-2 lg:px-3 py-1.5 lg:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-xs lg:text-sm"
                    data-testid="start-over-button"
                  >
                    最初から
                  </button>
                </div>
                <button
                  onClick={handleSave}
                  disabled={isLoading || selectedImages.length === 0}
                  className="w-full px-2 lg:px-3 py-1.5 lg:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-xs lg:text-sm"
                  data-testid="save-button"
                >
                  {isLoading ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompositeImageModal

